import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'astroturf/react';
import { SwitchTransition, Transition } from 'react-transition-group';
import { Set } from 'immutable';

import routes from 'sitewide-js/core_pre_webpack/routes';
import commaize from 'js/utils/commaize';
import usePrevious from 'js/utils/hooks/usePrevious';
import {
  abTest as paperFinderUpsellAbTest,
  TEST_CONFIG as PAPER_FINDER_UPSELL_TEST_CONFIG,
} from 'js/ab_tests/tests/demo_day_2026_paper_finder_vs_advanced_search';

import BlurredResults from './BlurredResults';
import {
  CountText,
  FADE_DURATION,
  transitions,
  TransitionState,
} from './fadeTransition';
import LoadingBase from './Loading';
import PeopleResultsBase from './people_results';
import PremiumIcon from 'js/premium/PremiumIcon';
import Results from './Results';
import SearchFiltersUpsellBase from '../SearchFiltersUpsell';
import Tabs, { TAB, tabText } from './Tabs';
import UpsellBase from './Upsell';
import UpsellModalRedesign from './UpsellModalRedesign';
import { Work } from './canonical_work_results';
import { Video } from 'js/videos/shared/video_helper';
import VideoResults from './VideoResults';
import CourseResults, { Course } from './CourseResults';
import { default as BaseFeedbackForm } from 'js/refutation_finder/FeedbackForm';

import {
  areFiltersDefault,
  getFilterOptions,
  HashifiedStats,
  isLangDefault,
  isPubTypeDefault,
  isVideoCategoryDefault,
  LangCode,
  langCodeToName,
  PubType,
  VideoCategory,
  redirectToMarketing,
  Stats,
} from './util';
import useUrlState from 'js/utils/hooks/useUrlState';
import LoadingDots from './LoadingDots';
import { Label, SearchFilters } from 'js/search/react/SearchFilters';
import {
  PaperFinderDashboardLink,
  PaperFinderUpsell,
} from './PaperFinderUpsell';
import {
  getSearchUpsellVisibility,
  SearchUpsellBucket,
  shouldTrackAdvancedSearchInline,
} from './searchUpsellExperiment';

type Props = {
  showUpsellModal?: boolean;
  onboardingOverhaulFulltext?: boolean;
  showFeedbackSurvey?: boolean;
  paperFinderDashboardUrl?: string;
  paperFinderUrl?: string;
  paperFinderPreviewUrl?: string;
  query: string;
};

type ApiSearchResults = {
  advanced_works?: Work[];
  counts?: ResultCounts;
  people?: { html: string }[];
  stats?: Stats;
  total?: number;
  works?: Work[];
  videos?: Video[];
  courses?: Course[];
};

/** If these are null, we show a loading indicator */
type ResultCounts = Record<
  'advanced_works' | 'people' | 'works' | 'videos' | 'courses',
  number | null
>;

const DOWN_ARROW = '\\25bc';
// This is the same MOBILE_WIDTH as is in Upsell
// They should be the same number
const MOBILE_WIDTH = 750;
// How many results to get from API per page
const PAGE_SIZE = 10;

const Container = styled.div`
  align-items: center;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const Count = styled.div<{ leftAlign?: boolean }>`
  grid-column: 2 / 3;
  margin: 0;

  &.leftAlign {
    grid-column: 1 / 3;
  }
`;

const Loading = styled(LoadingBase)<{ wide?: boolean }>`
  grid-column: span 2;
  // For mobile, otherwise the loading cards are mad squished
  width: 100%;

  // For loading people results, we need to take up
  // the whole width since we don't show filters
  &.wide {
    grid-column: 1 / 4;
  }
`;

const Main = styled.main`
  align-items: flex-start;
  display: grid;
  gap: 14px 30px;
  // 30% here is roughly 316px for the filters sidebar,
  // this percentage needs to be adjusted if the
  // max-width is changed.
  grid-template-columns: minmax(240px, 30%) 1fr 1fr;
  grid-template-rows: auto 1fr;
  // 1168px + 56px * 2
  max-width: 1280px;
  padding: 21px 56px 27px;
  width: 100%;

  @media (max-width: ${MOBILE_WIDTH}px) {
    display: flex;
    flex-direction: column;
    padding: 21px 24px 24px;
  }
`;

const MainContainer = styled.div`
  background: #f3f3f3;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const PageButton = styled.button`
  background: #fff;
  border-radius: 15px;
  border: 1px solid #ddd;
  color: #777;
  padding: 5px 14px;
  transition: 160ms background;

  &:hover {
    background: #eee;
  }
`;

const PageText = styled.p`
  color: #777;
  font-size: 12px;
  margin: 0 16px;
`;

const PaginationGroup = styled.div<{ fullWidth?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;
  grid-column: 2 / 4;
  // For mobile
  width: 100%;

  &.fullWidth {
    grid-column: 1 / 4;
  }
`;

const PeopleResults = styled(PeopleResultsBase)`
  // We don't show filters for people results,
  // so this makes the results take up the whole width
  grid-column: 1 / 4;
  // For mobile
  width: 100%;
`;

const SearchFiltersUpsell = styled(SearchFiltersUpsellBase)`
  grid-column: 2 / 4;
  // For mobile
  width: 100%;
`;

const Select = styled.select`
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  border-radius: 48px;
  border: 0;
  color: #4e545a;
  cursor: pointer;

  margin-left: 10px;
  padding: 1px 26px 1px 17px;

  &:hover {
    background: #ddd;
  }
`;

const SelectContainer = styled.div`
  position: relative;

  &::after {
    color: #4e545a;
    content: '${DOWN_ARROW}';
    font-size: 10px;
    right: 13px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const SortSelect = ({
  children,
  onChange,
}: {
  children: React.ReactNode;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}) => (
  <SelectContainer>
    <Select onChange={onChange}>{children}</Select>
  </SelectContainer>
);

const SortBy = styled.div`
  align-items: center;
  display: flex;
  justify-self: end;

  @media (max-width: ${MOBILE_WIDTH}px) {
    // We just don't show sorting on mobile
    display: none;
  }
`;

const TopGroup = styled.div`
  // 1168px + 56px * 2
  max-width: 1280px;
  padding: 22px 56px 0;
  width: 100%;

  @media (max-width: ${MOBILE_WIDTH}px) {
    padding: 22px 0 0;
  }
`;

const Upsell = styled(UpsellBase)`
  margin-bottom: 36px;
`;

const FeedbackForm = styled(BaseFeedbackForm)`
  grid-column: 2 / 4;
  width: 100%;

  background: var(--background-light-blue, #eef2f9);
  padding: 12px 16px;
  border: 1px solid #cddaef;
  border-radius: 4px;
`;

// Given the publication year counts, return the first and last year
const getFirstLastYears = (years: [number, number][]): [number, number] => {
  // Years are an array of tuples, in the form of
  // [year, number of works in that year]
  const first = Math.min(...years.map((ys) => ys[0]));
  const last = Math.max(...years.map((ys) => ys[0]));

  return [first, last];
};

const getNumPages = (tab: TAB, resultCounts: ResultCounts) => {
  let total = 0;
  if (tab === TAB.Titles) total = resultCounts.works || 0;
  if (tab === TAB.FullText) total = resultCounts.advanced_works || 0;
  if (tab === TAB.People) total = resultCounts.people || 0;
  if (tab === TAB.Videos) total = resultCounts.videos || 0;
  if (tab === TAB.Courses) total = resultCounts.courses || 0;

  return Math.ceil(total / PAGE_SIZE);
};

// Used to update number of pages when filters are used
// Pulls number of results from 'total' in the results object
const getNumPagesFiltered = (
  tab: TAB,
  resultCounts: ResultCounts,
  resultTotal: number | undefined,
) => {
  let total = 0;
  if (tab === TAB.People) {
    total = getNumPages(tab, resultCounts);
  } else {
    if (resultTotal === undefined) total = 0;
    else total = Math.ceil(resultTotal / PAGE_SIZE);
  }
  return total;
};

// Used for the `search_mode` param for the search API call
const getSearchMode = (tab: TAB) => {
  switch (tab) {
    case TAB.Titles:
      return 'works';
    case TAB.FullText:
      return 'advanced_works';
    case TAB.People:
      return 'people';
    case TAB.Videos:
      return 'videos';
    case TAB.Courses:
      return 'courses';
  }

  // eslint-disable-next-line no-console
  console.warn(`Unknown tab type ${tab}, using default of 'works'`);
  return 'works';
};

// Make all values for an object null
const nullify = (obj: object): object =>
  Object.fromEntries(Object.entries(obj).map(([k]) => [k, null]));

const shouldResetDateFilterData = (previousTab: TAB | undefined, tab: TAB) => {
  const groups = {
    // Group the 2 work tabs so that switching between them do not reset date filter data
    [TAB.Titles]: 1,
    [TAB.FullText]: 1,
    [TAB.People]: 2,
    // TAB.Translations (enum 3) has been removed
    [TAB.Videos]: 4,
    [TAB.Courses]: 5,
  };
  const currentGroup = groups[tab];
  const previousGroup = previousTab != null ? groups[previousTab] : 0;
  return currentGroup !== previousGroup;
};

const emptyResultCounts = {
  advanced_works: null,
  works: null,
  people: null,
  videos: null,
  courses: null,
};

const Search: React.FC<Props> = ({
  showUpsellModal: showUpsellModalInitial = false,
  onboardingOverhaulFulltext = false,
  showFeedbackSurvey = false,
  paperFinderDashboardUrl,
  paperFinderUrl,
  paperFinderPreviewUrl,
  query,
}) => {
  const dateFilterRef = useRef<{ resetYears(): void }>(null);
  const [resultCounts, setResultCounts] =
    useState<ResultCounts>(emptyResultCounts);
  const [loading, setLoading] = useState(false);
  // `pubType` can only have one value at once,
  // but the Filter component expects the `selected` prop to
  // be an array, since it supports multiple selections.
  // Whenever we set `pubType` we just make it an array with
  // one item
  const [pubType, setPubType] = useUrlState<[PubType]>(
    [PubType.All],
    'pubtype',
    {
      // Don't put into URL if pubtype is all
      stringify: (val) => (val[0] === PubType.All ? null : val),
      parse: (str) => [str] as [PubType],
    },
  );

  // `videoCategory` can only have one value at once,
  // but the Filter component expects the `selected` prop to
  // be an array, since it supports multiple selections.
  // Whenever we set `videoCategory` we just make it an array with
  // one item
  const [videoCategory, setVideoCategory] = useUrlState<[VideoCategory]>(
    [VideoCategory.All],
    'videoCategory',
    {
      // Don't put into URL if pubtype is all
      stringify: (val) => (val[0] === VideoCategory.All ? null : val),
      parse: (str) => [str] as [VideoCategory],
    },
  );

  // We use a set here to make it easier to work with multiple
  // values. `rawLangs` gets converted to an array (`langs`) before
  // being passed to the Filter component.
  const [rawLangs, setLang] = useUrlState<Set<LangCode>>(
    Set(['all']),
    'langs',
    {
      parse: (langsString) => Set(langsString.split(',') as LangCode[]),
      stringify: (set) => (set.has('all') ? null : set.toArray().toString()),
    },
  );

  let defaultTab = TAB.Titles;
  if (window.Aedu.User?.current?.premium) defaultTab = TAB.FullText;

  // Results is a hash, with potential properties:
  // `stats`, the info for the counts for the filters
  // `works`, `people`, or `advanced_works` based on the search mode
  // `count` the number of results for this query
  const [results, setResults] = useState<ApiSearchResults>({});
  const [sorting, setSorting] = useState('relevance');
  const [stats, setStats] = useState<HashifiedStats | null>(null);
  const [tab, setTab] = useUrlState<TAB>(defaultTab, 'tab', {
    // Coming from url, the tab will be a string, but we need a number
    parse: (s) => {
      const t = Number(s);
      if (t in TAB) {
        return t;
      }
      return window.Aedu.User?.current?.premium ? TAB.FullText : TAB.Titles;
    },
  });
  const previousTab = usePrevious(tab);
  const [[startYear, endYear], setDateRangeYears] = useUrlState<number[]>(
    [],
    'years',
    {
      parse: (yearString) => yearString.split(',').map(Number),
      stringify: (years) => {
        if (years.length === 0) return null;

        if (stats?.publicationYearCounts) {
          const [first, last] = getFirstLastYears(
            Object.entries(stats.publicationYearCounts) as unknown as [
              number,
              number,
            ][],
          );
          const [startYear, endYear] = years;
          if (
            Number(first) === Number(startYear) &&
            Number(last) === Number(endYear)
          ) {
            return null;
          }
        }

        return years.join(',');
      },
    },
  );

  const [page, setPage] = useUrlState(0, 'page', {
    parse: Number,
    stringify: (page) => (page === 0 ? null : String(page + 1)),
  });

  const [showUpsellModal, setShowUpsellModal] = useState(
    showUpsellModalInitial && window.matchMedia('(min-width: 1005px)').matches,
  );
  const advancedSearchModalIsCanonical = useRef(showUpsellModal).current;
  const advancedSearchUpsellEligible =
    !window.Aedu.User?.current?.premium &&
    !onboardingOverhaulFulltext &&
    (resultCounts.advanced_works || 0) > 0;
  const paperFinderExperimentEligible = Boolean(
    PAPER_FINDER_UPSELL_TEST_CONFIG.active &&
      paperFinderUrl &&
      advancedSearchUpsellEligible,
  );
  const paperFinderEligibilityPending = Boolean(
    PAPER_FINDER_UPSELL_TEST_CONFIG.active &&
      paperFinderUrl &&
      !onboardingOverhaulFulltext &&
      resultCounts.advanced_works == null,
  );
  const [upsellBucket, setUpsellBucket] = useState<SearchUpsellBucket>();

  useEffect(() => {
    if (!paperFinderExperimentEligible) return;

    const bucket = paperFinderUpsellAbTest();
    if (bucket !== 'holdout') setUpsellBucket(bucket);
  }, [paperFinderExperimentEligible]);

  const upsellVisibility = getSearchUpsellVisibility({
    advancedSearchEligible: advancedSearchUpsellEligible,
    advancedSearchModalOpen: showUpsellModal,
    paperFinderEligible:
      paperFinderExperimentEligible || paperFinderEligibilityPending,
    paperFinderEligibilityPending,
    paperFinderUrl,
    bucket: upsellBucket,
  });

  const langs = rawLangs.toArray();

  const clearFilters = () => {
    // Set all filters to their empty state/default values
    setDateRangeYears([]);
    setPubType([PubType.All]);
    setLang(Set(['all']));
    setVideoCategory([VideoCategory.All]);
    // DateFilter holds the state for its years internally,
    // so it exposes a function to reset its filter years
    // so we can call it here
    dateFilterRef.current?.resetYears();
  };

  const toggleLang = (lang: LangCode) => {
    setLang((langs) => {
      const newLangs = langs.withMutations((langs) => {
        // If we're selecting a new language, we should deselect All
        if (lang !== 'all') {
          langs.remove('all');
        }

        // Toggle
        if (langs.has(lang)) {
          langs.remove(lang);
        } else {
          langs.add(lang);
        }

        // If the user selects All, we deselect all other langs
        if (lang === 'all') {
          langs.clear();
          langs.add('all');
        }

        // If the user has just deselected the last language they've selected,
        // we reselect All
        if (langs.count() === 0) {
          langs.add('all');
        }
      });

      return newLangs;
    });
  };

  // When we select a filter, the API response doesn't send down stas
  // for the rest of the filters, so here we just merge in any new
  // responses with the existing stats
  const updateStats = (stats: Stats, resetDateFilterData: boolean) => {
    const hashified: HashifiedStats = {
      languageCounts: Object.fromEntries(stats.languageCounts) as {
        [key in LangCode]: number;
      },
      publicationTypeCounts: Object.fromEntries(
        stats.publicationTypeCounts || [],
      ) as { [key in PubType]: number },
      publicationYearCounts: Object.fromEntries(stats.publicationYearCounts),
      videoCategoryCounts: Object.fromEntries(
        stats.videoCategoryCounts || [],
      ) as { [key in VideoCategory]: number },
    };
    setStats((s) => {
      if (s == null) {
        return hashified;
      }
      const publicationYearCounts = resetDateFilterData
        ? {}
        : s['publicationYearCounts'];
      return {
        // We null out existing stats here so the filter options for them stay,
        // but we don't have accurate counts for them so we just want to hide them
        languageCounts: {
          ...nullify(s['languageCounts'] ?? {}),
          ...hashified['languageCounts'],
        },
        publicationTypeCounts: {
          ...nullify(s['publicationTypeCounts'] ?? {}),
          ...hashified['publicationTypeCounts'],
        },
        videoCategoryCounts: {
          ...nullify(s['videoCategoryCounts'] ?? {}),
          ...hashified['videoCategoryCounts'],
        },
        // Don't null out year counts so it shows them grayed out
        publicationYearCounts: {
          ...publicationYearCounts,
          ...hashified['publicationYearCounts'],
        },
      };
    });
    resetDateFilterData && setDateRangeYears([]);
  };

  // If a user is not premium and changes filters,
  // we hide results and show an upsell
  const showGate =
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'User' does not exist on type 'typeof Aed... Remove this comment to see the full error message
    !Aedu?.User?.current?.premium &&
    (!areFiltersDefault({
      langs,
      pubType,
      videoCategory,
      years: [startYear, endYear],
    }) ||
      (!onboardingOverhaulFulltext && tab === TAB.FullText));

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ left: 0, top: 0 });
  }, [page]);

  const searchEffectHook = (resetDateFilterData = false) => {
    const aborter = new AbortController();
    const signal = aborter.signal;
    const params: {
      camelize_keys: boolean;
      canonical: boolean;
      fake_results: string;
      json: boolean;
      language?: LangCode[];
      last_seen: string;
      offset: number;
      pub_after_date?: number;
      pub_before_date?: number;
      publication_type?: PubType[];
      video_category?: VideoCategory[];
      query: string;
      search_mode: string;
      size: number;
      sort: string;
      subdomain_param: string;
      user_language: string;
      referring_hit_id?: number;
    } = {
      camelize_keys: true,
      canonical: true,
      fake_results: 'null',
      // PeopleResults component just renders html sent from the server.
      // This is less than ideal but it works for now until we can rewrite that
      json: tab !== TAB.People,
      language: langs,
      last_seen: 'null',
      offset: page * PAGE_SIZE,
      pub_after_date: startYear,
      pub_before_date: endYear,
      publication_type: pubType,
      video_category: videoCategory,
      query,
      search_mode: getSearchMode(tab),
      size: PAGE_SIZE,
      sort: sorting,
      subdomain_param: 'api',
      user_language: navigator.language.split('-')[0],
    };

    const hitId = window.Aedu?.hit_data?.id;
    if (hitId) {
      params.referring_hit_id = hitId;
    }

    if (isLangDefault(params.language)) {
      delete params.language;
    }

    if (isPubTypeDefault(params.publication_type)) {
      delete params.publication_type;
    }

    if (isVideoCategoryDefault(params.video_category)) {
      delete params.video_category;
    }

    if (params.pub_after_date == null) {
      delete params.pub_after_date;
    }

    if (params.pub_before_date == null) {
      delete params.pub_before_date;
    }

    setLoading(true);
    fetch(routes.api_v0_search_integrated_search_path(params), { signal })
      .then((res) => {
        if (res.ok) return res.json();
        throw res.status;
      })
      .then((data) => {
        const { stats, ...results } = data as ApiSearchResults;

        setResults(results);
        if (stats != null) {
          updateStats(stats, resetDateFilterData);
        }
      })
      .catch((err) => {
        // Ignore the error if it's caused by the
        // abort controller
        if (err instanceof DOMException) return;
        else throw err;
      })
      .catch((err) => {
        if (err === 404) {
          // Clear results (but keep stats and total) if we get a 404, which means
          // the user is non premium but has selected full-text search,
          // so we want to show the upsell.
          setResults((r) => ({ stats: r.stats, total: r.total }));
        }
      })
      .finally(() => setLoading(false));

    // This prevents fetching after unmount, and also cancels
    // requests if a new request is made
    return () => {
      aborter.abort();
    };
  };

  const firstUpdate = useRef(true);

  // Updates Page number every time the TAB changes
  // or when the results change
  const numPages = useMemo(() => {
    return getNumPagesFiltered(tab, resultCounts, results.total);
  }, [results, tab, resultCounts]);

  // we want to make sure this doesn't conflict with the searchEffectHook callback directly below
  useEffect(() => {
    // Reset back to the first page, if any parameters are updated (fixes #22390):
    if (firstUpdate.current) {
      firstUpdate.current = false;
    } else {
      setPage(0);
      searchEffectHook(shouldResetDateFilterData(previousTab, tab));
    }
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endYear,
    pubType,
    rawLangs,
    showGate,
    sorting,
    startYear,
    tab,
    videoCategory,
  ]);

  // Otherwise, if we are just going forward or back a page:
  // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(searchEffectHook, [page]);

  // Populate counts for tabs, upsell, and upsell modal
  useEffect(() => {
    // If we already have counts on mount, this means we are in the
    // control bucket and these counts have already come from the server
    if (resultCounts.works !== null) return;

    fetch(
      routes.api_v0_search_estimated_counts_path({
        subdomain_param: 'api',
        query,
        user_language: navigator.language.split('-')[0],
      }),
    )
      .then((res) => res.json())
      .then(({ counts }) => setResultCounts(counts));
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const workResults = results?.works ?? results?.advanced_works;
  const options = getFilterOptions(stats, {
    pubType,
    langs,
    videoCategory,
  });

  // Get the count of results after filters have been applied
  // WARNING: kinda hacky and may break if we change how the
  // counts in `options` are calculated
  const getFilteredResultsCount = () => {
    switch (tab) {
      // These tabs don't have filters, so just return the original count
      case TAB.People:
        return resultCounts.people || 0;
      case TAB.Courses:
        return resultCounts.courses || 0;
      // Video count is busted at the moment because of a different bug
      case TAB.Videos:
        return resultCounts.videos || 0;
      default:
        return options.langs
          .filter((l) => langs.includes(l.value))
          .reduce((sum, l) => sum + (l.count || 0), 0);
    }
  };
  const filteredResultCount = getFilteredResultsCount();

  const fullWidth = [TAB.People, TAB.Courses].includes(tab);

  return (
    <Container>
      {upsellVisibility.advancedSearchModal && (
        <UpsellModalRedesign
          advancedCount={resultCounts.advanced_works}
          trackExperiment={upsellBucket === 'advanced_search'}
          onClose={() => setShowUpsellModal(false)}
          query={query}
          workCount={resultCounts.works}
        />
      )}

      <TopGroup>
        {paperFinderDashboardUrl && (
          <PaperFinderDashboardLink
            query={query}
            url={paperFinderDashboardUrl}
          />
        )}

        {!PAPER_FINDER_UPSELL_TEST_CONFIG.active && paperFinderUrl && (
          <PaperFinderUpsell
            query={query}
            url={paperFinderUrl}
            previewUrl={paperFinderPreviewUrl}
          />
        )}

        {PAPER_FINDER_UPSELL_TEST_CONFIG.active &&
          upsellVisibility.paperFinder &&
          paperFinderUrl && (
            <PaperFinderUpsell
              query={query}
              url={paperFinderUrl}
              previewUrl={paperFinderPreviewUrl}
              trackExperiment
            />
          )}

        {upsellVisibility.advancedSearchInline && (
          <Upsell
            count={resultCounts.advanced_works || 0}
            query={query}
            trackExperiment={shouldTrackAdvancedSearchInline(
              upsellBucket,
              advancedSearchModalIsCanonical,
            )}
          />
        )}

        <Tabs
          counts={{
            [TAB.FullText]: resultCounts.advanced_works,
            [TAB.People]: resultCounts.people,
            [TAB.Titles]: resultCounts.works,
            [TAB.Videos]: resultCounts.videos,
            [TAB.Courses]: resultCounts.courses,
          }}
          current={tab}
          onChange={setTab}
          onboardingOverhaulFulltext={onboardingOverhaulFulltext}
        />
      </TopGroup>

      <MainContainer>
        <Main>
          {![TAB.People, TAB.Courses].includes(tab) && (
            <SearchFilters
              startYear={startYear}
              endYear={endYear}
              dateFilterRef={dateFilterRef}
              setDateRangeYears={setDateRangeYears}
              stats={stats}
              loading={loading}
              langs={{
                onChange: (v: string) => toggleLang(v as LangCode),
                options: options.langs,
                selected: langs,
              }}
              pubTypes={
                tab === TAB.Videos
                  ? undefined
                  : {
                      onChange: (value: string) =>
                        setPubType([value as PubType]),
                      options: options.pubTypes,
                      selected: pubType,
                    }
              }
              videoCategories={
                tab !== TAB.Videos
                  ? undefined
                  : {
                      onChange: (value: string) =>
                        setVideoCategory([value as VideoCategory]),
                      options: options.videoCategories,
                      selected: videoCategory,
                    }
              }
            />
          )}

          {showFeedbackSurvey && [TAB.Titles, TAB.FullText].includes(tab) && (
            <FeedbackForm searchQuery={query} />
          )}

          <Count leftAlign={fullWidth}>
            <SwitchTransition>
              <Transition key={loading.toString()} timeout={FADE_DURATION}>
                {(state: TransitionState) =>
                  !loading ? (
                    <CountText style={transitions[state]}>
                      <strong>
                        {tabText(
                          tab,
                          filteredResultCount,
                          !areFiltersDefault({
                            langs,
                            pubType,
                            videoCategory,
                            years: [startYear, endYear],
                          }),
                        )}
                      </strong>{' '}
                      {filteredResultCount !== 1 ? 'match' : 'matches'}{' '}
                      <strong>{query}</strong>
                    </CountText>
                  ) : (
                    <LoadingDots
                      size={7}
                      // Have to put the transition in the style here
                      // since the way LoadingDots is written, using styled(LoadingDots)
                      // affects the individual dots, not the container
                      style={{
                        ...transitions[state],
                        transition: `${FADE_DURATION}ms opacity`,
                      }}
                    />
                  )
                }
              </Transition>
            </SwitchTransition>
          </Count>

          {![TAB.People].includes(tab) && (
            <SortBy>
              <PremiumIcon size={20} />
              <Label>Sort By</Label>
              <SortSelect
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  // @ts-expect-error ts-migrate(2339) FIXME: Property 'User' does not exist on type 'typeof Aed... Remove this comment to see the full error message
                  if (Aedu.User?.current?.premium) {
                    setSorting(e.target.value);
                  } else {
                    redirectToMarketing(
                      query,
                      resultCounts.advanced_works,
                      'search-sort-by',
                    );
                  }
                }}
              >
                <option value="relevance">Relevance</option>
                <option
                  value={
                    [TAB.Videos, TAB.Courses].includes(tab)
                      ? 'recency'
                      : 'publication'
                  }
                >
                  Recency
                </option>
              </SortSelect>
            </SortBy>
          )}

          {showGate && tab === TAB.Titles && (
            <SearchFiltersUpsell
              languages={
                rawLangs.has('all') ? undefined : langs.map(langCodeToName)
              }
              numResults={filteredResultCount}
              onClear={clearFilters}
              pubAfterDate={startYear}
              pubBeforeDate={endYear}
              publicationType={
                pubType[0] === PubType.All ? undefined : pubType[0]
              }
              query={query}
              tab={tab}
            />
          )}

          {/* Renders a distinct version of the upsell for full text,
              so each impression can trigger independently */}
          {showGate && tab === TAB.FullText && (
            <SearchFiltersUpsell
              languages={
                rawLangs.has('all') ? undefined : langs.map(langCodeToName)
              }
              numResults={filteredResultCount}
              onClear={clearFilters}
              pubAfterDate={startYear}
              pubBeforeDate={endYear}
              publicationType={
                pubType[0] === PubType.All ? undefined : pubType[0]
              }
              query={query}
              tab={tab}
            />
          )}

          {/* Renders a distinct version of the upsell for videos,
              so each impression can trigger independently */}
          {showGate && tab === TAB.Videos && (
            <SearchFiltersUpsell
              languages={
                rawLangs.has('all') ? undefined : langs.map(langCodeToName)
              }
              numResults={filteredResultCount}
              onClear={clearFilters}
              pubAfterDate={startYear}
              pubBeforeDate={endYear}
              publicationType={
                pubType[0] === PubType.All ? undefined : pubType[0]
              }
              query={query}
              tab={tab}
            />
          )}

          {loading && !showGate && <Loading wide={fullWidth} />}

          {!loading &&
            !showGate &&
            [TAB.Titles, TAB.FullText].includes(tab) &&
            workResults &&
            workResults.length > 0 && <Results works={workResults} />}

          {!loading && !showGate && tab === TAB.Videos && results?.videos && (
            <VideoResults videos={results.videos} />
          )}

          {!loading && !showGate && tab === TAB.Courses && results?.courses && (
            <CourseResults courses={results.courses} />
          )}

          {showGate && tab !== TAB.People && filteredResultCount > 0 && (
            <BlurredResults />
          )}
          {results?.people && tab === TAB.People && (
            <PeopleResults people={results?.people} />
          )}

          {!showGate && (
            <PaginationGroup fullWidth={fullWidth}>
              {page !== 0 && (
                <PageButton onClick={() => setPage((p) => Math.max(p - 1, 0))}>
                  Prev
                </PageButton>
              )}
              <PageText>
                {/* `page` is 0 indexed */}
                Page {commaize(page + 1)} of {commaize(Math.max(numPages, 1))}
              </PageText>
              {page + 1 < numPages && (
                <PageButton onClick={() => setPage((p) => p + 1)}>
                  Next
                </PageButton>
              )}
            </PaginationGroup>
          )}
        </Main>
      </MainContainer>
    </Container>
  );
};

export default Search;
